import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { format } from "date-fns";
import DataRestoreBanner from "@/components/account/DataRestoreBanner";
import QuickProgressLog from "@/components/dashboard/QuickProgressLog";
import RotatingQuote from "@/components/dashboard/RotatingQuote";
import WeeklyCheckIn from "@/components/dashboard/WeeklyCheckIn";
import TodaysWorkout from "@/components/dashboard/TodaysWorkout";
import DailyNutritionSection from "@/components/dashboard/DailyNutritionSection";
import YourProgressSection from "@/components/dashboard/YourProgressSection";
import UpcomingSessions from "@/components/dashboard/UpcomingSessions";

function getTimeOfDayGreeting() {
  const h = new Date().getHours();
  if (h < 5) return { text: "Good night", emoji: "🌙" };
  if (h < 12) return { text: "Good morning", emoji: "🌅" };
  if (h < 17) return { text: "Good afternoon", emoji: "☀️" };
  if (h < 21) return { text: "Good evening", emoji: "🌇" };
  return { text: "Good night", emoji: "🌙" };
}

export default function Dashboard() {
  const { user } = useAuth();
  const todayDate = format(new Date(), "yyyy-MM-dd");
  const greeting = getTimeOfDayGreeting();
  const firstName = user?.full_name?.trim()?.split(" ")[0] || "Member";

  // Sessions query
  const { data: sessions = [] } = useQuery({
    queryKey: ["sessions"],
    queryFn: () =>
      user
        ? base44.entities.TrainingSession.filter(
            { created_by_id: user.id },
            "-date",
            50
          )
        : [],
    enabled: !!user,
  });

  // Trainers query
  const { data: trainers = [] } = useQuery({
    queryKey: ["trainers"],
    queryFn: () => base44.entities.Trainer.list(),
  });

  // Workouts and meals counts for progress pills
  const { data: workouts = [] } = useQuery({
    queryKey: ["workouts"],
    queryFn: () => base44.entities.WorkoutPlan.list(),
  });

  const { data: meals = [] } = useQuery({
    queryKey: ["meals"],
    queryFn: () => base44.entities.MealPlan.list(),
  });

  // Prefetch workout and exercise completions for fast rendering
  useQuery({
    queryKey: ["workout-completions", todayDate],
    queryFn: () =>
      base44.entities.WorkoutCompletion.filter({
        user_id: user.id,
        completed_date: todayDate,
      }),
    enabled: !!user,
  });

  useQuery({
    queryKey: ["exercise-completions", todayDate],
    queryFn: () =>
      base44.entities.ExerciseCompletion.filter({
        user_id: user.id,
        completed_date: todayDate,
      }),
    enabled: !!user,
  });

  useQuery({
    queryKey: ["exercises"],
    queryFn: () => base44.entities.Exercises.list("name", 2500),
  });

  const upcomingCount = sessions.filter((s) => s.status === "upcoming").length;
  const completedCount = sessions.filter((s) => s.status === "completed").length;
  const activeWorkouts = workouts.filter((w) => w.is_active).length;
  const activeMeals = meals.filter((m) => m.is_active).length;

  return (
    <div className="space-y-4 sm:space-y-5 pb-8 select-none">
      {/* Self-serve data restore banner (only renders if user has recent archive) */}
      <DataRestoreBanner />

      {/* 1. PERSONALIZED GREETING & PAGE HEADER */}
      <div className="flex items-center justify-between pt-1 gap-3">
        <div className="min-w-0 flex-1">
          <span className="text-[10px] sm:text-[11px] font-bold tracking-widest text-sky-400 uppercase block">
            Your Dashboard
          </span>
          <h1
            className="text-2xl sm:text-3xl font-display font-black tracking-tight text-white truncate"
            data-testid="welcome-heading"
          >
            {greeting.text}, {firstName}.
          </h1>
          <RotatingQuote />
        </div>
        <QuickProgressLog />
      </div>

      {/* 3. DAILY CHECK-IN ACHIEVEMENT */}
      <WeeklyCheckIn />

      {/* 4. TODAY'S WORKOUT (APPROVED 290PX CINEMATIC HERO) */}
      <TodaysWorkout />

      {/* 5. DAILY NUTRITION & 6. YOUR NEXT MEAL */}
      <DailyNutritionSection />

      {/* 7. YOUR PROGRESS */}
      <YourProgressSection
        upcomingSessionsCount={upcomingCount}
        completedSessionsCount={completedCount}
        activeWorkoutsCount={activeWorkouts}
        activeMealsCount={activeMeals}
      />

      {/* 8. UPCOMING SESSIONS */}
      <UpcomingSessions sessions={sessions} trainers={trainers} />
    </div>
  );
}